import { forwardRef } from "react";
import { Link } from "react-router-dom";

/**
 * AppButton — Microsoft Surface-style pill (rounded-full) button.
 *
 * Variants reflect category color logic:
 *   - primary / blue   (#0E5FB0)
 *   - purple           (#5B2E83)  → Mobility / JapanETCcard
 *   - green            (#2F8F3C)  → Records / JapanTollReceipts
 *   - secondary        (white pill, navy border)
 *   - outline          (navy pill, transparent fill)
 *   - ghost            (text-only, underline-grow)
 */
const SIZE = {
  sm: "h-9 px-4 text-[12px]",
  md: "h-11 px-5 text-[13px]",
  lg: "h-12 px-6 text-[14px]",
};

const VARIANT = {
  primary: "bg-[#0E5FB0] text-white border border-[#0E5FB0] hover:bg-[#084785] hover:border-[#084785]",
  blue: "bg-[#0E5FB0] text-white border border-[#0E5FB0] hover:bg-[#084785] hover:border-[#084785]",
  purple: "bg-[#5B2E83] text-white border border-[#5B2E83] hover:bg-[#3F1F5B] hover:border-[#3F1F5B]",
  green: "bg-[#78D33C] text-white border border-[#78D33C] hover:bg-[#5FB825] hover:border-[#5FB825]",
  secondary: "bg-white text-[#0F1D2D] border border-[#CBD5E1] hover:border-[#0F1D2D]",
  outline: "bg-transparent text-[#5B2E83] border border-[#5B2E83] hover:bg-[#5B2E83] hover:text-white",
  ghost: "bg-transparent text-[#0F1D2D] border border-transparent hover:text-[#0E5FB0]",
};

const AppButton = forwardRef(function AppButton(
  { children, variant = "primary", size = "md", to, href, type = "button", className = "", disabled = false, leadingIcon: Leading, trailingIcon: Trailing, "data-testid": testId, ...rest },
  ref,
) {
  const base = [
    "group inline-flex items-center justify-center gap-2 font-semibold tracking-tight",
    "rounded-full transition-colors duration-200 ease-out",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0E5FB0] focus-visible:ring-offset-2 focus-visible:ring-offset-white",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    SIZE[size] || SIZE.md,
    VARIANT[variant] || VARIANT.primary,
    className,
  ].join(" ");

  const content = (
    <>
      {Leading ? <Leading className="h-3.5 w-3.5" strokeWidth={1.75} /> : null}
      <span className="leading-none">{children}</span>
      {Trailing ? (
        <Trailing className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" strokeWidth={1.75} />
      ) : null}
    </>
  );

  if (to) {
    return (
      <Link ref={ref} to={to} data-testid={testId} className={base} aria-disabled={disabled || undefined} {...rest}>
        {content}
      </Link>
    );
  }
  if (href) {
    return (
      <a ref={ref} href={href} data-testid={testId} className={base} {...rest}>
        {content}
      </a>
    );
  }
  return (
    <button ref={ref} type={type} data-testid={testId} className={base} disabled={disabled} {...rest}>
      {content}
    </button>
  );
});

export default AppButton;
